import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SplashIntro } from "@/components/SplashIntro";
import { EditableText } from "@/context/ContentContext";
import { SERVICES } from "@/data/site";

export default function Home() {
  return (
    <div data-testid="home-page">
      <SplashIntro />
      {/* HERO */}
      <section className="relative pt-36 md:pt-48 pb-24 md:pb-32 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <EditableText k="home_eyebrow" as="p" className="font-mono2 uppercase tracking-[0.3em] text-darkgrey mb-8" />
        </motion.div>
        <h1 className="font-heading leading-[0.95] tracking-tight max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <EditableText k="home_title1" as="span" className="block" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <EditableText k="home_title2" as="span" className="block" />
          </motion.div>
        </h1>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
          <EditableText k="home_subtitle" as="p" className="font-editorial text-darkgrey mt-10 max-w-2xl leading-relaxed" />
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
          className="flex flex-wrap gap-4 mt-12">
          <Link to="/servizi" data-testid="hero-servizi-cta"
            className="group border border-ink text-ink px-8 py-4 uppercase text-xs tracking-widest flex items-center gap-3 hover:bg-ink hover:text-greige transition-colors">
            I servizi <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/contatti" data-testid="hero-contatti-cta"
            className="border border-ink px-8 py-4 uppercase text-xs tracking-widest hover:bg-ink hover:text-greige transition-colors">
            Lavoriamo insieme
          </Link>
        </motion.div>
        <div className="hidden md:flex items-center gap-3 mt-24 font-mono2 text-xs uppercase tracking-widest text-darkgrey">
          <ArrowDown size={14} /> Pochi clienti, seguiti in profondità.
        </div>
      </section>

      {/* INTRO */}
      <section className="border-y border-darkgrey/30 bg-paper/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-3">
            <EditableText k="home_intro_eyebrow" as="p" className="font-mono2 uppercase tracking-widest text-darkgrey" />
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-8 md:col-start-5">
            <EditableText k="home_intro_title" as="p" className="font-heading leading-tight" />
            <EditableText k="home_intro_body" as="p" className="font-body text-darkgrey mt-8 leading-relaxed max-w-2xl" />
          </Reveal>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <div className="flex items-end justify-between mb-12">
            <EditableText k="home_services_title" as="h2" className="font-heading" />
            <Link to="/servizi" className="link-underline font-mono2 text-xs uppercase tracking-widest hidden md:block">Tutti i servizi →</Link>
          </div>
        </Reveal>
        <div className="border-t border-darkgrey/30">
          {SERVICES.slice(0, 6).map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <Link to="/servizi" className="group grid grid-cols-12 gap-4 items-baseline py-7 border-b border-darkgrey/30 hover:bg-paper/50 transition-colors px-2 -mx-2">
                <span className="col-span-2 font-mono2 text-sm text-darkgrey">{s.n}</span>
                <span className="col-span-10 md:col-span-7 font-heading text-2xl md:text-3xl hover-bold-noshift">{s.title}</span>
                <span className="hidden md:block col-span-3 text-right text-darkgrey group-hover:translate-x-1 transition-transform"><ArrowRight className="inline" size={18} /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-greige">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <EditableText k="home_cta_eyebrow" as="p" className="font-mono2 uppercase tracking-widest text-greige/50 mb-6" />
            <EditableText k="home_cta_title" as="h2" className="font-heading leading-tight max-w-3xl" />
            <Link to="/contatti" data-testid="cta-contatti"
              className="inline-flex items-center gap-3 mt-10 bg-greige text-ink px-8 py-4 uppercase text-xs tracking-widest hover:bg-white transition-colors">
              Scrivimi <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
